import {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {IGetAIReviewJobResultResponse} from '../../../../../api/types/aiReview.type';
import {DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB} from '../../../../../shared/types';
import {numberFormatter} from '../../../../../utils/utils';
import JobResultMetricsStrip, {
  JobResultMetricItem,
} from './JobResultMetricsStrip';

type IssueCodingJobResultMetricsProps = {
  metricsDetails?: IGetAIReviewJobResultResponse;
  variant?: 'issueCoding' | 'relevancy';
  onDocErrorPanelOpen?: () => void;
  onNavigateToDocList?: (
    docsType: DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB,
    totalDocs?: number
  ) => void;
};

const IssueCodingJobResultMetrics: FC<IssueCodingJobResultMetricsProps> = ({
  metricsDetails,
  variant = 'issueCoding',
  onDocErrorPanelOpen,
  onNavigateToDocList,
}) => {
  const {t} = useTranslation();
  const erroredCount = metricsDetails?.errored ?? metricsDetails?.Errored;
  const isRelevancy = variant === 'relevancy';
  const excludedFromAiFileTypes = [
    t('ai.review.metrics.csvType'),
    t('ai.review.metrics.multiMediaType'),
    t('ai.review.metrics.ArchieveType'),
    t('ai.review.metrics.databaseType'),
    t('ai.review.metrics.unKnownType'),
    t('ai.review.metrics.systemType'),
    t('ai.review.metrics.missingTextFileType'),
  ];
  const excludedFromAiTooltip = (
    <div className="excluded-from-ai-tooltip">
      <p>{t('ai.review.metrics.ecludedAiTooltipText')}</p>
      <ul>
        {excludedFromAiFileTypes.map((fileType) => (
          <li key={fileType}>{fileType}</li>
        ))}
      </ul>
    </div>
  );

  const formatMetricValue = (value: number | undefined) =>
    numberFormatter.format(value ?? 0);

  const metrics: JobResultMetricItem[] = [
    {
      id: 'documents-in-selected-source',
      displayValue: formatMetricValue(
        metricsDetails?.documentsInSelectedSource
      ),
      label: t('ai.review.jobResult.header.metrics.docsInSelectedSource'),
      ariaLabel: `${t('ai.review.jobResult.header.metrics.docsInSelectedSource')} ${formatMetricValue(metricsDetails?.documentsInSelectedSource)}`,
    },
    {
      id: 'excluded-from-ai',
      displayValue: formatMetricValue(metricsDetails?.excludedFromAI),
      label: t('ai.review.jobResult.header.metrics.excludedFromAi'),
      showInfoTooltip: true,
      tooltip: excludedFromAiTooltip,
      isClickable: (metricsDetails?.excludedFromAI ?? 0) > 0,
      onClick: () =>
        onNavigateToDocList?.(
          DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB.EXCLUDED,
          metricsDetails?.excludedFromAI
        ),
      ariaLabel: `${
        (metricsDetails?.excludedFromAI ?? 0) > 0
          ? `${t('common.clickable')} `
          : ''
      }${t('ai.review.jobResult.header.metrics.excludedFromAi')} ${formatMetricValue(metricsDetails?.excludedFromAI)}`,
    },
    {
      id: 'submitted-to-ai',
      displayValue: formatMetricValue(metricsDetails?.submittedToAI),
      label: t('ai.review.jobResult.header.metrics.submittedToAi'),
      ariaLabel: `${t('ai.review.jobResult.header.metrics.submittedToAi')} ${formatMetricValue(metricsDetails?.submittedToAI)}`,
    },
    {
      id: 'completed',
      displayValue: formatMetricValue(metricsDetails?.completed),
      label: t('ai.review.jobResult.header.metrics.completed'),
      isClickable: (metricsDetails?.completed ?? 0) > 0,
      onClick: () =>
        onNavigateToDocList?.(
          DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB.COMPLETED,
          metricsDetails?.completed
        ),
      ariaLabel: `${
        (metricsDetails?.completed ?? 0) > 0 ? `${t('common.clickable')} ` : ''
      }${t('ai.review.jobResult.header.metrics.completed')} ${formatMetricValue(metricsDetails?.completed)}`,
    },
    {
      id: 'errored',
      displayValue: formatMetricValue(erroredCount),
      label: t('ai.review.jobResult.header.metrics.errored'),
      showInfoTooltip: true,
      tooltip: t('ai.review.metrics.erroredTooltipText'),
      isClickable: (erroredCount ?? 0) > 0,
      onClick: () => {
        if ((erroredCount ?? 0) === 0) {
          return;
        }
        onDocErrorPanelOpen?.();
      },
      ariaLabel: `${
        (erroredCount ?? 0) > 0 ? `${t('common.clickable')} ` : ''
      }${t('ai.review.jobResult.header.metrics.errored')} ${formatMetricValue(erroredCount)}`,
    },
  ];

  if (isRelevancy) {
    metrics.push(
      {
        id: 'relevant',
        displayValue: formatMetricValue(metricsDetails?.relevant),
        label: t('ai.review.jobResult.header.metrics.relevant'),
        isClickable: (metricsDetails?.relevant ?? 0) > 0,
        onClick: () =>
          onNavigateToDocList?.(
            DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB.RELEVANT,
            metricsDetails?.relevant
          ),
        ariaLabel: `${
          (metricsDetails?.relevant ?? 0) > 0 ? `${t('common.clickable')} ` : ''
        }${t('ai.review.jobResult.header.metrics.relevant')} ${formatMetricValue(metricsDetails?.relevant)}`,
      },
      {
        id: 'not-relevant',
        displayValue: formatMetricValue(metricsDetails?.nonRelevant),
        label: t('ai.review.jobResult.header.metrics.notRelevant'),
        isClickable: (metricsDetails?.nonRelevant ?? 0) > 0,
        onClick: () =>
          onNavigateToDocList?.(
            DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB.NON_RELEVANT,
            metricsDetails?.nonRelevant
          ),
        ariaLabel: `${
          (metricsDetails?.nonRelevant ?? 0) > 0
            ? `${t('common.clickable')} `
            : ''
        }${t('ai.review.jobResult.header.metrics.notRelevant')} ${formatMetricValue(metricsDetails?.nonRelevant)}`,
      }
    );
  }

  return <JobResultMetricsStrip metrics={metrics} />;
};

export default IssueCodingJobResultMetrics;
